import type { OutgoingAttachment } from '../api/chat.js';

/**
 * Documentos adjuntos: se leen en el navegador y viajan con el mensaje. No se
 * suben a ningun almacenamiento: el servidor se los pasa al modelo y los olvida.
 *
 * Las fotos del movil pesan 4-12 MB y el modelo no gana nada con tanta
 * resolucion: se reducen a 2000 px por el lado mayor (un sello o una firma se
 * siguen leyendo) y se recomprimen en JPEG. Los PDF van tal cual.
 */

export interface PendingFile extends OutgoingAttachment {
  id: string;
  size: number;
}

export const ACCEPT = 'application/pdf,image/jpeg,image/png,image/webp,.pdf,.jpg,.jpeg,.png,.webp';
export const MAX_FILES = 5;
const MAX_BYTES = 10 * 1024 * 1024;
const MAX_SIDE = 2000;

const toBase64 = (buffer: ArrayBuffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
};

async function shrinkImage(file: File): Promise<{ data: string; mediaType: string; size: number }> {
  const bitmap = await createImageBitmap(file).catch(() => null);
  if (!bitmap) {
    const buffer = await file.arrayBuffer();
    return { data: toBase64(buffer), mediaType: file.type, size: buffer.byteLength };
  }
  const k = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * k);
  canvas.height = Math.round(bitmap.height * k);
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#fff'; // un PNG con transparencia no debe quedar negro en JPEG
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.86));
  if (!blob) throw new Error('no se pudo procesar la imagen');
  const buffer = await blob.arrayBuffer();
  return { data: toBase64(buffer), mediaType: 'image/jpeg', size: buffer.byteLength };
}

/** Lee los archivos elegidos. Devuelve los validos y, si alguno no lo es, el motivo. */
export async function readFiles(
  list: FileList | File[],
  already: number,
): Promise<{ files: PendingFile[]; problem: string | null }> {
  const files: PendingFile[] = [];
  let problem: string | null = null;
  for (const file of Array.from(list)) {
    if (already + files.length >= MAX_FILES) {
      problem = `Puedes adjuntar hasta ${MAX_FILES} documentos por mensaje.`;
      break;
    }
    const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
    const isImage = /^image\/(jpeg|png|webp)$/.test(file.type) || /\.(jpe?g|png|webp)$/i.test(file.name);
    if (!isPdf && !isImage) {
      problem = `«${file.name}» no es un PDF ni una foto (JPG, PNG o WEBP).`;
      continue;
    }
    try {
      if (isPdf) {
        if (file.size > MAX_BYTES) {
          problem = `«${file.name}» pesa más de 10 MB.`;
          continue;
        }
        const buffer = await file.arrayBuffer();
        files.push({ id: `${Date.now()}-${files.length}`, name: file.name, mediaType: 'application/pdf', data: toBase64(buffer), size: buffer.byteLength });
      } else {
        const img = await shrinkImage(file);
        if (img.size > MAX_BYTES) {
          problem = `«${file.name}» pesa más de 10 MB.`;
          continue;
        }
        files.push({ id: `${Date.now()}-${files.length}`, name: file.name, ...img });
      }
    } catch {
      problem = `No se pudo leer «${file.name}».`;
    }
  }
  return { files, problem };
}

export const formatSize = (bytes: number) =>
  bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`;
